import { useCallback, useEffect, useMemo, useState } from 'react'

import type { ITableItem, TUseBaseTableProps } from './base-table.types'

export const useBaseTable = ({
  colors,
  data,
  rowLimit,
  customSort,
  isCustomAsc,
  currentActive,
  defaultSort,
}: TUseBaseTableProps) => {
  const [tableData, setTableData] = useState<ITableItem[]>([])
  const [activeSort, setActiveSort] = useState('')
  const [isAsc, setIsAsc] = useState(true)

  const colorToId = useMemo(() => {
    const colorsMap = new Map()
    data?.forEach((item, index) => {
      colorsMap.set(item.id, colors?.[index])
    })
    return colorsMap
  }, [data, colors])

  const rowList = useMemo(() => {
    if (rowLimit) {
      const list = tableData
      return list?.slice(0, rowLimit)
    }
    return tableData
  }, [tableData, rowLimit])

  const compare = useCallback((a: any, b: any, key: string, order: boolean) => {
    const itemOne = a[key]
    const itemTwo = b[key]
    let result = 0

    if (typeof itemOne === 'number') {
      if (itemOne < itemTwo) result = -1
      else if (itemOne > itemTwo) result = 1
      else result = 0
    } else {
      result = itemOne.localeCompare(itemTwo)
    }

    return order ? result : -result
  }, [])

  const handleSort = (key: string) => {
    if (activeSort === key) {
      const copy = new Array(...tableData)
      const sortedData = copy.sort((a, b) => compare(a, b, key, !isAsc))
      setIsAsc(oldState => !oldState)
      setTableData(sortedData)
    } else {
      setActiveSort(key)
      const copy = new Array(...tableData)
      const sortedData = copy.sort((a, b) => compare(a, b, key, true))
      setIsAsc(true)
      setTableData(sortedData)
    }
  }

  const getIsAsc = () => {
    if (customSort) {
      return isCustomAsc
    }
    return isAsc
  }

  const isItemAsc = getIsAsc()

  const getIsCurrentSelected = (key: string) => {
    const isCurrentSelected = key === activeSort || (key === currentActive && currentActive)
    return Boolean(isCurrentSelected)
  }

  useEffect(() => {
    if (data) {
      if (defaultSort) {
        const copy = new Array(...data)
        const sortedData = copy.sort((a, b) => compare(a, b, defaultSort, false))
        setTableData(sortedData)
        setActiveSort(defaultSort)
        setIsAsc(false)
      } else {
        setTableData(data)
        setActiveSort('')
        setIsAsc(true)
      }
    }
  }, [compare, data, defaultSort])

  return {
    colorsMap: colorToId,
    rowList,
    handleSort,
    isItemAsc,
    getIsCurrentSelected,
  }
}
